import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  feeSubscriptionPlanCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { feeEngineEndpoints } from "@/features/fee-engine/api/endpoints";
import type { SubscriptionPlanDto } from "@/features/fee-engine/api/dto";

/**
 * Crée un plan d'abonnement entreprise (`POST /SubscriptionPlans/add`,
 * guide §6.5) — borné par un effectif min/max.
 */
export const useCreateSubscriptionPlan = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["fee-engine", "subscription-plans", "create"],
    mutationFn: (body: SubscriptionPlanDto) =>
      mutateData({
        url: feeEngineEndpoints.subscriptionPlans.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<SubscriptionPlanDto>,
    onSuccess: () => {
      return invalidateAfterMutation(
        queryClient,
        feeSubscriptionPlanCacheKeys(),
        { resetListPage: true }
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateSubscriptionPlan failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "fee-engine" }
      );
    },
  });
};
